import { batch, computed, effect, signal } from '@preact/signals'
import * as engine from '../core/engine'
import { DEFAULT_DAY, clampDaySettings, isNight, isNightWake, nextDayStart, type DaySettings } from '../core/day'
import { planReminders, type Reminder } from '../core/reminders'
import { t } from '../i18n'
import { weightPoints } from '../core/weight'
import { wakeWindows, windowClosedAt, type WakeWindow } from '../core/wake-windows'
import type { SleepSpan } from '../core/day-stats'
import { forProfile } from '../core/programs'
import type { ChildProfile, Phase, WakeState } from '../core/types'
import { addEvent, hiddenStarts, loadEvents, markEventDeleted, replaceEvent, type DayEvent, type EndReason } from '../stores/events'
import { scheduleSync } from '../api/sync'
import type { Platform } from '../platform/platform'
import type { Storage } from '../platform/storage'
import { addNote as addNoteToStore, loadNotes, removeNote as removeNoteFromStore, updateNote as updateNoteInStore, type Note } from '../stores/notes'
import { loadWindows, logWindow } from '../stores/history'
import { applyCustomPhases, clearCustomPhases, isCustom as isCustomPhases, saveCustomPhases } from '../stores/phases'
import { loadSchedules, saveSchedules } from '../stores/schedules'
import { defaultScheduleName, napHint as подсказкаСна, programForSchedule, scheduleIndexFor, windowsToday, type NapHint, type WakeSchedule } from '../core/schedules'
import { loadProfile, saveProfile as saveProfileToStore } from '../stores/profile'
import { loadWindowStart, saveWindowStart } from '../stores/window'
import { markSelfCheckFinding } from '../stores/selfcheck'
import { KEY } from '../stores/keys'
import { tellServerTourSeen } from '../api/account'
import { defaultProgram } from '../core/programs'
import { rememberKid, switchKid } from '../stores/kids-boot'

/**
 * Состояние приложения. Хранилище подключается при старте, дальше все действия
 * пишут в него и обновляют сигналы; отдельного «тикающего» таймера нет —
 * время живёт в сигнале [now], а фаза и отсчёт вычисляются от него.
 */

export type Tab = 'timer' | 'myDay' | 'calendar' | 'menu'

/** Вкладка календаря: лента месяцев или аналитика веса. Профиль умеет открыть вес напрямую. */
export const calendarTab = signal<'month' | 'weight'>('month')
/** Раздел аналитики: окна бодрствования или вес. Бодрствование первым — это то, ради чего приложение. */
export const analyticsKind = signal<'wake' | 'weight'>('wake')
/** Короткое сообщение внизу экрана из любого места приложения; оболочка показывает и сбрасывает. */
export const notice = signal<string | null>(null)
/** День, лист которого календарь откроет при показе (сцена холста «лист по дате»). */
export const calendarDayToOpen = signal<number | null>(null)

export const now = signal(Date.now())
export const tab = signal<Tab>('timer')
export const profile = signal<ChildProfile | null>(null)
export const windowStart = signal<number | null>(null)
export const customPhases = signal<Phase[] | null>(null)
export const notes = signal<Note[]>([])
export const windows = signal<number[]>([])
/** Журнал: фазы, концы окон, ночные пробуждения, взвешивания. */
export const events = signal<DayEvent[]>([])
/** Режим дня: границы дня и напоминание о подъёме (настройки «Режим дня»). */
export const daySettings = signal<DaySettings>(DEFAULT_DAY)
/** Малыш спит с этого момента — ответ «да, уснул». От него считаются напоминания о подъёме. */
export const sleepingSince = signal<number | null>(null)
/**
 * Пробуем продлить сон: малыш заворочался, родитель укачивает. Момент начала попытки;
 * null — попытки нет. Удалось — сон идёт дальше, минуты попытки остаются сном; не удалось —
 * бодрствование начинается с этого момента (просьба заказчика 27.10.2026).
 */
export const sleepExtendSince = signal<number | null>(null)
/** Сколько даём на попытку продлить сон. */
export const SLEEP_EXTEND_MS = 20 * 60_000
export const ready = signal(false)

let storage: Storage | null = null
let platform: Platform | null = null

/** Рекомендованная программа по возрасту; пересчитывается раз в час вместе с [now]. */
export const baseProgram = computed(() => {
  const p = profile.value
  return p === null ? defaultProgram() : forProfile(p, now.value)
})

/** Добавленные ВБ 2…N для текущей программы; ВБ 1 — это [customPhases] поверх рекомендованной (27.10.2026). */
export const wakeSchedules = signal<WakeSchedule[]>([])
/** Номер ВБ идущего окна (решён при его старте); null — окно не идёт или начато до появления ВБ (тогда ВБ 1). */
export const windowSchedule = signal<number | null>(null)
/** Начало окна, у которого ВБ выбрали руками; null — номер решён счётом окон. */
const выбранВручную = signal<number | null>(null)

/** Начала окон, которые видны в календаре: история без спрятанных правкой плюс вписанные руками. */
const видимыеНачала = computed(() => {
  const скрытые = hiddenStarts(events.value)
  const руками = events.value.filter((e) => e.deleted !== true && e.kind === 'window_end' && e.manual === true).map((e) => e.window)
  return [...new Set([...windows.value.filter((at) => !скрытые.has(at)), ...руками])]
})

/** Номер ВБ для окна, если начать его прямо сейчас: по счёту окон с начала дня. 0 — график кончился, по возрасту. */
export const nextScheduleIndex = computed(() => scheduleIndexFor(windowsToday(видимыеНачала.value, now.value, daySettings.value), wakeSchedules.value))
/**
 * Номер ВБ окна по счёту окон дня до него. Запасной путь: своей записи нет, но считать,
 * что это первое окно дня, нельзя — именно так экран показывал ВБ 1 третьему окну.
 */
const номерПоСчёту = (start: number) => scheduleIndexFor(windowsToday(видимыеНачала.value, start, daySettings.value), wakeSchedules.value)

/** Номер ВБ, по которому живёт экран: идущего окна или следующего. */
export const activeScheduleIndex = computed(() =>
  windowStart.value !== null ? (windowSchedule.value ?? номерПоСчёту(windowStart.value)) : nextScheduleIndex.value,
)

/** Программа с учётом правок «Моего дня» и того, какое ВБ сейчас идёт. */
export const program = computed(() => programForSchedule(baseProgram.value, customPhases.value, wakeSchedules.value, activeScheduleIndex.value))

export const state = computed<WakeState>(() => ({ program: program.value, windowStartMillis: windowStart.value }))

export const phaseIndex = computed(() => engine.currentIndex(state.value, now.value))
export const isRunning = computed(() => windowStart.value !== null)
/** Сколько секунд последняя фаза идёт сверх плана. По времени окно не закрывается. */
export const overtime = computed(() => engine.overtimeSeconds(state.value, now.value))
/**
 * Чем и когда кончилось последнее окно в этом запуске приложения: после «да, уснул»
 * экран «Готовы?» желает доброго сна, после сброса без ответа — говорит, во сколько окно
 * закрылось. Не хранится: при следующем открытии это уже прошлое.
 */
export const justClosed = signal<{ reason: EndReason; at: number } | null>(null)
/** Ночное пробуждение идёт: момент нажатия «Проснулся» ночью. Без фаз и уведомлений. */
export const nightWake = signal<number | null>(null)
/**
 * «Сколько спать» по графику ВБ: только пока малыш спит днём и впереди ещё есть внесённые ВБ.
 * Номер законченного окна — счёт окон дня до момента засыпания включительно.
 */
export const napHint = computed<NapHint | null>(() => {
  const since = sleepingSince.value
  if (since === null || windowStart.value !== null || nightWake.value !== null) return null
  const k = windowsToday(видимыеНачала.value, since + 1, daySettings.value)
  return подсказкаСна(since, k, wakeSchedules.value)
})

/**
 * Какой по счёту дневной сон идёт: после ВБ 1 — первый. Считается по окнам дня до
 * засыпания, как и [napHint], но не зависит от графика: номер нужен и без внесённых ВБ
 * (просьба заказчика 31.10.2026). Без окон за день номера нет.
 *
 * Ночь проверяется и по засыпанию, и по часам — как у кнопки «Продлить сон». Одного
 * засыпания мало: в 3–4 месяца на ночь укладывают и до 20:30, и такой сон держал «5-й за
 * день» всю ночь и до утра.
 */
export const napNumber = computed<number | null>(() => {
  const since = sleepingSince.value
  if (since === null || windowStart.value !== null || nightWake.value !== null) return null
  if (isNight(since, daySettings.value) || isNight(now.value, daySettings.value)) return null
  const k = windowsToday(видимыеНачала.value, since + 1, daySettings.value)
  return k > 0 ? k : null
})

/** Сейчас ночь по правилам дня: «Проснулся» запишется ночным пробуждением, а не окном. */
export const isNightNow = computed(() => isNightWake(now.value, daySettings.value))
export const currentPhase = computed(() => program.value.phases[phaseIndex.value] ?? null)
export const isCustomProgram = signal(false)

/**
 * Показывали ли человеку тур «Как это работает».
 *
 * Раньше тур решался на лету в конце анкеты приложения: «профиля не было — значит,
 * человек новый». С сайта это не работало — там регистрация идёт на лендинге, и в
 * приложение человек приходил уже с профилем, минуя тур. Теперь это записанный факт:
 * на устройстве и, если есть аккаунт, на сервере — чтобы на втором телефоне тур
 * не начинался заново.
 */
export const tourSeen = signal(false)

/** Тур досмотрели или пропустили. */
export async function markTourSeen(): Promise<void> {
  tourSeen.value = true
  if (storage !== null) await storage.set(KEY.tourSeen, '1')
  await tellServerTourSeen()
}

/** Сервер сказал, что тур человеку уже показывали (вошёл на другом устройстве). */
export async function rememberTourSeen(): Promise<void> {
  if (tourSeen.value) return
  tourSeen.value = true
  if (storage !== null) await storage.set(KEY.tourSeen, '1')
}

/** Подключает хранилище и читает всё, что в нём лежит. */
export async function init(target: Storage | Platform): Promise<void> {
  const isPlatform = (value: Storage | Platform): value is Platform => 'stateChanged' in value
  platform = isPlatform(target) ? target : null
  storage = isPlatform(target) ? target.storage : target
  target = storage
  const loaded = await loadProfile(target)
  const start = await loadWindowStart(target)
  const base = loaded === null ? defaultProgram() : forProfile(loaded, Date.now())
  const phases = (await applyCustomPhases(target, base)).phases
  // Читаем всё до того, как убрать заставку. Раньше экран оживал после анкеты, а список
  // окон дня и список ВБ дочитывались следом — и нажатие в эту щель решало, что окно
  // первое за день. ВБ 1 застревал на всё окно (28.10.2026). Экран, показанный раньше
  // данных, — это всегда чья-то неверная цифра, и пауза тут честнее.
  const ночное = await загрузитьНочное(target)
  const турПоказан = (await target.get(KEY.tourSeen)) === '1'
  const свои = await isCustomPhases(target, base.id)
  const заметки = await loadNotes(target)
  const окна = await loadWindows(target)
  const журнал = await loadEvents(target)
  const режим = await загрузитьРежимДня(target)
  const спит = await загрузитьЧисло(target, KEY.sleepingSince)
  const продление = await загрузитьЧисло(target, KEY.sleepExtendSince)
  const графики = await loadSchedules(target, base.id)
  const номерОкна = await загрузитьЧисло(target, KEY.windowSchedule)
  const вручную = await загрузитьЧисло(target, KEY.windowScheduleManual)
  batch(() => {
    profile.value = loaded
    windowStart.value = start
    customPhases.value = phases === base.phases ? null : phases
    isCustomProgram.value = свои
    nightWake.value = ночное
    tourSeen.value = турПоказан
    notes.value = заметки
    windows.value = окна
    events.value = журнал
    daySettings.value = режим
    sleepingSince.value = спит
    sleepExtendSince.value = продление
    wakeSchedules.value = графики
    windowSchedule.value = номерОкна
    выбранВручную.value = вручную
    ready.value = true
  })
  await починитьНомерОкна(target)
  // Изменение пришло извне: с часов, из другой вкладки или от будильника.
  // Перечитываем экраны и обязательно отдаём это серверу — иначе окно, начатое
  // на часах, осталось бы только на телефоне.
  target.onChange?.(() => {
    версияХранилища += 1
    if (!перечитываем) void refresh().then(() => scheduleSync())
  })
}

/**
 * Переключение на другого малыша: экран гаснет, пока не прочитаны его данные.
 *
 * Само переключение только поворачивает хранилище к другой приставке — мгновенно. А на
 * экране ещё висит прошлый малыш: его окно, его фазы, его номер ВБ. Нажатие в этот
 * промежуток записало бы решение про одного ребёнка в данные другого. Тот же урок, что и
 * на запуске: экран, показанный раньше данных, — это не пустота, а чужая цифра (28.10.2026).
 */
export async function switchToKid(id: string): Promise<boolean> {
  if (!(await switchKid(id))) return false
  ready.value = false
  try {
    await refresh()
  } finally {
    ready.value = true
  }
  return true
}

/**
 * Правка истории меняет счёт окон дня — значит и номер идущего окна.
 *
 * Номер решается при старте окна и сам по себе не едет (решение заказчика 27.10.2026).
 * Но если человек убрал окно, вписал его руками или сдвинул начало, счёт изменился по его
 * воле, и номер обязан последовать сразу. Раньше он ждал перезапуска: 29.10.2026 мама
 * убрала окно, и до следующего открытия приложения на экране был чужой график.
 *
 * Это не разлад, а следствие осознанного действия, поэтому находку не помечаем.
 */
async function пересчитатьНомерОкна(): Promise<void> {
  if (storage === null) return
  const start = windowStart.value
  if (start === null || wakeSchedules.value.length === 0) return
  // ВБ выбрали руками — это решение человека, счёт окон его не перебивает
  if (выбранВручную.value === start) return
  const надо = номерПоСчёту(start)
  if (надо === windowSchedule.value) return
  windowSchedule.value = надо
  await storage.set(KEY.windowSchedule, String(надо))
}

/**
 * Поправляет номер идущего окна, если он был решён вслепую.
 *
 * Пока заставка уходила раньше чтения, нажатие «Проснулся» в эту щель записывало ВБ 1:
 * окон дня приложение ещё не видело. Окно живёт часами, и всё это время человек смотрит
 * на чужой график. Считаем номер заново по времени старта окна — на запуске это безопасно:
 * правок истории ещё не было, а дальше номер снова держится и сам не поедет (28.10.2026).
 */
async function починитьНомерОкна(target: Storage): Promise<void> {
  const start = windowStart.value
  if (start === null || wakeSchedules.value.length === 0) return
  // Выбранное руками — не разлад: чинить и сообщать нечего
  if (выбранВручную.value === start) return
  const надо = номерПоСчёту(start)
  const было = windowSchedule.value
  if (надо === было) return
  windowSchedule.value = надо
  await target.set(KEY.windowSchedule, String(надо))
  // Поправили молча — но знать об этом должны мы, и знать с подробностями: уведомление,
  // по которому нельзя понять причину, бесполезно (просьба заказчика 29.10.2026).
  // «Правок» — сколько окон дня спрятано правкой истории: если их нет, расхождение наше.
  const окна = windowsToday(видимыеНачала.value, start, daySettings.value)
  const правок = [...hiddenStarts(events.value)].filter((at) => at >= start - СУТКИ && at < start).length
  // Строка английская, как остальные записи журнала сервера: её читаем мы, не человек.
  // edits — сколько окон дня спрятано правкой истории; ноль означает, что виноваты мы.
  await markSelfCheckFinding(
    target,
    'window_schedule_wrong',
    `was ${было ?? 'none'}, now ${надо}, windows ${окна}, edits ${правок}, schedules ${wakeSchedules.value.length + 1}, start ${новоеВремя(start)}`,
  )
}

const СУТКИ = 24 * 3600 * 1000
/** Время без даты: в подробностях находки нужен час, а не момент эпохи. */
const новоеВремя = (at: number): string => {
  const d = new Date(at)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * Стирает всё, что знает устройство о ребёнке. Нужно там, где данные показывать больше
 * нельзя: аккаунт удалён или человек вышел из него.
 *
 * Ключ устройства для уведомлений не трогаем — это не данные человека, а адрес доставки:
 * тот же телефон сможет подписаться заново, не заводя новую запись на сервере.
 */
export async function wipeLocal(): Promise<void> {
  if (storage === null) return
  for (const key of await storage.keys()) {
    if (key === 'device_token') continue
    await storage.remove(key)
  }
  batch(() => {
    profile.value = null
    windowStart.value = null
    customPhases.value = null
    isCustomProgram.value = false
    notes.value = []
    windows.value = []
    events.value = []
    daySettings.value = DEFAULT_DAY
    sleepingSince.value = null
    justClosed.value = null
    nightWake.value = null
  })
}

/**
 * Перечитать всё из хранилища: изменение пришло извне (часы, другая вкладка, будильник).
 *
 * Сначала всё читается, потом присваивается разом. Раньше начало окна на время чтения
 * обнулялось — и действие, шедшее в этот момент, видело «окна нет»: так в журнал попадала
 * фаза с номером −1, которую сервер отвергал вместе со всей посылкой (23–24.10.2026).
 *
 * Чтение, начатое до очередной записи, не должно затереть более свежее состояние: если за
 * время чтения хранилище менялось — читаем заново и присваиваем только свежее.
 */
let версияХранилища = 0
let перечитываем = false
export async function refresh(): Promise<void> {
  if (storage === null) return
  заново: for (;;) {
    const версия = версияХранилища
    перечитываем = true
    const s = storage
    const loaded = await loadProfile(s)
    const base = loaded === null ? defaultProgram() : forProfile(loaded, Date.now())
    const start = await loadWindowStart(s)
    const ночное = await загрузитьНочное(s)
    const applied = await applyCustomPhases(s, base)
    const свои = await isCustomPhases(s, base.id)
    const заметки = await loadNotes(s)
    const окна = await loadWindows(s)
    const журнал = await loadEvents(s)
    const режим = await загрузитьРежимДня(s)
    const спит = await загрузитьЧисло(s, KEY.sleepingSince)
    const продление = await загрузитьЧисло(s, KEY.sleepExtendSince)
    const графики = await loadSchedules(s, base.id)
    const номерОкна = await загрузитьЧисло(s, KEY.windowSchedule)
    const вручную = await загрузитьЧисло(s, KEY.windowScheduleManual)
    if (версия !== версияХранилища) continue заново
    batch(() => {
      profile.value = loaded
      windowStart.value = start
      nightWake.value = ночное
      customPhases.value = applied.phases === base.phases ? null : applied.phases
      isCustomProgram.value = свои
      notes.value = заметки
      windows.value = окна
      events.value = журнал
      daySettings.value = режим
      sleepingSince.value = спит
      sleepExtendSince.value = продление
      wakeSchedules.value = графики
      windowSchedule.value = номерОкна
      выбранВручную.value = вручную
    })
    перечитываем = false
    return
  }
}

export function startTicking(intervalMs = 1000): () => void {
  const id = setInterval(() => {
    now.value = Date.now()
  }, intervalMs)
  const onVisible = () => {
    if (document.visibilityState === 'visible') now.value = Date.now()
  }
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    clearInterval(id)
    document.removeEventListener('visibilitychange', onVisible)
  }
}

async function загрузитьЧисло(target: Storage, key: string): Promise<number | null> {
  const raw = await target.get(key)
  const at = raw === null ? Number.NaN : Number(raw)
  return Number.isFinite(at) && at > 0 ? at : null
}

const загрузитьНочное = (target: Storage) => загрузитьЧисло(target, KEY.nightWakeStart)

async function загрузитьРежимДня(target: Storage): Promise<DaySettings> {
  return clampDaySettings({
    dayStartMin: (await загрузитьЧисло(target, KEY.dayStartMin)) ?? DEFAULT_DAY.dayStartMin,
    bedtimeMin: (await загрузитьЧисло(target, KEY.bedtimeMin)) ?? DEFAULT_DAY.bedtimeMin,
    wakePush: (await target.get(KEY.wakePush)) !== '0',
  })
}

/** Настройки «Режима дня»: сохранить и пересчитать напоминания. */
export async function saveDaySettings(next: DaySettings): Promise<void> {
  const clamped = clampDaySettings(next)
  daySettings.value = clamped
  if (storage === null) return
  await storage.set(KEY.dayStartMin, String(clamped.dayStartMin))
  await storage.set(KEY.bedtimeMin, String(clamped.bedtimeMin))
  await storage.set(KEY.wakePush, clamped.wakePush ? '1' : '0')
  // Часы получают границы дня вместе с профилем, а профиль едет по своей метке времени
  await storage.set(KEY.profileUpdatedAt, String(Date.now()))
  await announceState()
}

/** Запись в журнал — и сигнал следом, чтобы календарь увидел её сразу. */
async function запись(target: Storage, event: DayEvent): Promise<void> {
  await addEvent(target, event)
  events.value = await loadEvents(target)
}

/**
 * Отсчёт сна на таймере идёт от записи о засыпании: поправили её любым способом — через
 * окно бодрствования или через строку сна — отсчёт идёт от новой записи. Иначе после правки
 * 19:46 → 19:20 таймер показывал «спит 4 мин» (жалоба заказчика 27.10.2026).
 */
async function выровнятьОтсчётСна(): Promise<void> {
  if (sleepingSince.value === null || windowStart.value !== null || profile.value === null) return
  const последнее = wakeWindows(events.value, windows.value, profile.value).at(-1)
  if (последнее === undefined || !последнее.confirmed || последнее.end === null) return
  if (последнее.end !== sleepingSince.value) await записатьСон(последнее.end)
}

/** Спит с этого момента или не спит: хранится, чтобы напоминания пережили перезапуск. */
async function записатьСон(at: number | null): Promise<void> {
  sleepingSince.value = at
  // Сна нет — нет и попытки его продлить
  if (at === null) sleepExtendSince.value = null
  if (storage === null) return
  if (at === null) {
    await storage.remove(KEY.sleepingSince)
    await storage.remove(KEY.sleepExtendSince)
  } else await storage.set(KEY.sleepingSince, String(at))
}

/** «Продлить сон»: малыш заворочался, пробуем укачать — отсчёт на двадцать минут. */
export async function startSleepExtension(): Promise<void> {
  if (sleepingSince.value === null) return
  const at = Date.now()
  sleepExtendSince.value = at
  if (storage !== null) await storage.set(KEY.sleepExtendSince, String(at))
}

/** «Уснул»: продлить удалось — сон идёт дальше, минуты попытки остаются сном. */
export async function sleepExtended(): Promise<void> {
  sleepExtendSince.value = null
  if (storage !== null) await storage.remove(KEY.sleepExtendSince)
}

/** Напоминания из текущего состояния: их считает приложение, сервер лишь доставляет. */
function напоминания(): Reminder[] {
  const child = profile.value
  if (child === null) return []
  return planReminders({
    profile: child,
    program: program.value,
    settings: daySettings.value,
    now: Date.now(),
    asleepAt: sleepingSince.value,
    weights: weightPoints(events.value, child.birthMillis, child.birthWeightGrams),
  })
}

/** Убрать взвешивание: ошибку на весах можно исправить. Запись остаётся меткой ради обмена. */
export async function removeWeight(event: DayEvent): Promise<void> {
  if (storage === null || event.kind !== 'weight') return
  await markEventDeleted(storage, event)
  events.value = await loadEvents(storage)
  scheduleSync()
  await announceState()
}

/**
 * Окно бодрствования, вписанное руками в календаре (просьба 12.10.2026).
 *
 * Одна запись на всё окно: `window` — начало, `at` — конец, причина «уснул» — чтобы оно
 * шло в аналитику наравне с отсчитанными таймером.
 */
export async function addManualWindow(start: number, end: number): Promise<void> {
  if (storage === null || end <= start) return
  await запись(storage, { id: end, kind: 'window_end', at: end, window: start, reason: 'asleep', manual: true })
  await пересчитатьНомерОкна()
  scheduleSync()
  await announceState()
}

/** Запись о конце окна [at], которая видна: не удалённая и самая ранняя — как считает аналитика. */
function конецОкна(at: number): DayEvent | undefined {
  return events.value
    .filter((e) => e.kind === 'window_end' && e.window === at && e.deleted !== true)
    .sort((a, b) => a.at - b.at)[0]
}

/**
 * Правка времени окна (просьба заказчика 20.10.2026).
 *
 * Прежний конец помечается удалённым, новое время ложится ручной записью с ответом «уснул».
 * У окна таймера запись несёт `replaces` — его начало: историю начал не правим (её читают
 * часы и виджет, а в обмене нет удаления начал), поэтому прежнее начало прячется меткой.
 * У уже поправленного окна метка переезжает в новую запись, чтобы начало не вернулось.
 */
export async function editWindow(w: WakeWindow, start: number, end: number, comment: { note?: string } = {}): Promise<void> {
  if (storage === null || end <= start) return
  const старая = конецОкна(w.at)
  const replaces = старая?.replaces ?? (w.manual ? undefined : w.at)
  const поля = {
    kind: 'window_end' as const,
    at: end,
    window: start,
    reason: 'asleep' as const,
    manual: true,
    ...(replaces === undefined ? {} : { replaces }),
    // Сон описывает та же запись: его комментарий и место сохраняем как были
    ...(старая?.place === undefined ? {} : { place: старая.place }),
    ...(старая?.note === undefined ? {} : { note: старая.note }),
    ...(comment.note === undefined ? {} : { wakeNote: comment.note }),
  }
  // Свою же ручную правку переписываем на месте: иначе каждое сохранение оставляло
  // новую запись, а прежнюю помечало удалённой — восемь сохранений, восемь записей
  // (у жены заказчика ровно так и вышло 29.10.2026). Запись таймера трогать нельзя:
  // историю начал мы не правим, прежнее начало прячется меткой.
  if (старая !== undefined && старая.manual === true && старая.deleted !== true) {
    await replaceEvent(storage, { ...старая, ...поля }, старая.at)
    events.value = await loadEvents(storage)
  } else {
    if (старая !== undefined) await markEventDeleted(storage, старая)
    await запись(storage, { id: Date.now(), ...поля })
  }
  await выровнятьОтсчётСна()
  await пересчитатьНомерОкна()
  scheduleSync()
  await announceState()
}

/** Видимая запись о конце пробуждения: самая ранняя из неудалённых (так же считает аналитика). */
function концовка(at: number, ночное: boolean): DayEvent | undefined {
  const kind = ночное ? 'night_wake_end' : 'window_end'
  return events.value.filter((e) => e.kind === kind && e.window === at && e.deleted !== true).sort((a, b) => a.at - b.at)[0]
}

/**
 * Правка времени сна (просьба заказчика 21.10.2026).
 *
 * У сна нет своей записи: начало — это конец предыдущего бодрствования, конец — следующее
 * «Проснулся». Поэтому правим обе границы по отдельности и только то, что человек изменил:
 * начало переписывает запись о засыпании (вместе с местом и комментарием ко сну), конец
 * двигает начало следующего пробуждения.
 */
export async function editSleep(s: SleepSpan, from: number, to: number, comment: { place?: string; note?: string } = {}): Promise<void> {
  if (storage === null || to <= from) return
  const старая = концовка(s.windowAt, s.afterNightWake)

  // ---- начало сна: запись о засыпании ----
  if (s.afterNightWake) {
    if (старая !== undefined) await markEventDeleted(storage, старая)
    await запись(storage, {
      id: Date.now(),
      kind: 'night_wake_end',
      at: from,
      window: s.windowAt,
      reason: 'asleep',
      ...(comment.place === undefined ? {} : { place: comment.place }),
      ...(comment.note === undefined ? {} : { note: comment.note }),
      ...(старая?.wakeNote === undefined ? {} : { wakeNote: старая.wakeNote }),
    })
  } else {
    const окно = wakeWindows(events.value, windows.value, profile.value!).find((w) => w.at === s.windowAt)
    if (окно !== undefined) {
      if (старая !== undefined) await markEventDeleted(storage, старая)
      const replaces = старая?.replaces ?? (окно.manual ? undefined : окно.at)
      await запись(storage, {
        id: Date.now(),
        kind: 'window_end',
        at: from,
        window: окно.start,
        reason: 'asleep',
        manual: true,
        ...(replaces === undefined ? {} : { replaces }),
        ...(comment.place === undefined ? {} : { place: comment.place }),
        ...(comment.note === undefined ? {} : { note: comment.note }),
        ...(старая?.wakeNote === undefined ? {} : { wakeNote: старая.wakeNote }),
      })
    }
  }

  // ---- конец сна: начало следующего пробуждения ----
  if (to !== s.to) await двинутьПробуждение(s.to, to)

  events.value = await loadEvents(storage)
  await выровнятьОтсчётСна()
  await пересчитатьНомерОкна()
  scheduleSync()
  await announceState()
}

/**
 * Перенести момент пробуждения с [было] на [стало].
 *
 * Ночное пробуждение — пара записей, переписываем обе. Окно таймера живёт началом в истории:
 * у закрытого переписываем запись о конце (она и несёт начало), у незакрытого прячем прежнее
 * начало и дописываем новое в историю — конца у него нет, и выдумывать его нельзя.
 */
async function двинутьПробуждение(было: number, стало: number): Promise<void> {
  if (storage === null || было === стало) return
  const ночное = events.value.find((e) => e.kind === 'night_wake' && e.at === было && e.deleted !== true)
  if (ночное !== undefined) {
    await markEventDeleted(storage, ночное)
    await запись(storage, { id: Date.now(), kind: 'night_wake', at: стало, window: стало })
    const конец = концовка(было, true)
    if (конец !== undefined) {
      await markEventDeleted(storage, конец)
      await запись(storage, { ...конец, id: Date.now() + 1, window: стало, deleted: false })
    }
    return
  }
  const окно = wakeWindows(events.value, windows.value, profile.value!).find((w) => w.at === было)
  if (окно === undefined) return
  const конец = концовка(было, false)
  if (конец !== undefined) {
    await markEventDeleted(storage, конец)
    await запись(storage, { ...конец, id: Date.now() + 2, window: стало, replaces: конец.replaces ?? окно.at, manual: true, deleted: false })
    return
  }
  // Окно ещё не закрыто: прячем прежнее начало и дописываем новое в историю
  await запись(storage, { id: Date.now() + 3, kind: 'window_end', at: было, window: было, reason: 'reset', manual: true, replaces: было, deleted: true })
  await logWindow(storage, стало)
  windows.value = await loadWindows(storage)
  if (windowStart.value === было) await persistWindow(стало)
}

/**
 * Убрать окно целиком. Вписанное или поправленное руками — удалить его запись. У окна
 * таймера конца может и не быть, а начало лежит в истории, поэтому его прячет отдельная
 * запись: удалённая, с `replaces`. Необратимо, как любое удаление в журнале.
 */
export async function removeWindow(w: WakeWindow): Promise<void> {
  if (storage === null) return
  const старая = конецОкна(w.at)
  if (старая !== undefined) await markEventDeleted(storage, старая)
  if (!w.manual) {
    await запись(storage, { id: Date.now(), kind: 'window_end', at: w.at, window: w.at, reason: 'reset', manual: true, replaces: w.at, deleted: true })
  }
  events.value = await loadEvents(storage)
  await пересчитатьНомерОкна()
  scheduleSync()
  await announceState()
}

/** Убрать окно, вписанное руками: ошибку во времени исправляют. */
export async function removeManualWindow(event: DayEvent): Promise<void> {
  if (storage === null || event.kind !== 'window_end' || event.manual !== true) return
  await markEventDeleted(storage, event)
  events.value = await loadEvents(storage)
  await пересчитатьНомерОкна()
  scheduleSync()
  await announceState()
}

/** Взвешивание: запись в журнал, обмен и пересчёт ориентира по весу. */
export async function addWeight(at: number, grams: number): Promise<void> {
  if (storage === null) return
  await запись(storage, { id: at, kind: 'weight', at, window: at, grams })
  scheduleSync()
  await announceState()
}

// ---- действия ----

async function persistWindow(next: number | null): Promise<void> {
  now.value = Date.now()
  if (next !== null && windowStart.value === null) {
    // Новое окно: какое ВБ ему достаётся, решается сейчас и запоминается — правка истории
    // посреди окна его уже не сдвинет (27.10.2026). Кончился график — по возрасту, и говорим об этом.
    const k = nextScheduleIndex.value
    windowSchedule.value = k
    выбранВручную.value = null
    if (storage !== null) {
      await storage.set(KEY.windowSchedule, String(k))
      await storage.remove(KEY.windowScheduleManual)
    }
    if (k === 0 && wakeSchedules.value.length > 0) notice.value = t('schedule_fallback_notice')
  }
  if (next === null && windowStart.value !== null) {
    windowSchedule.value = null
    выбранВручную.value = null
    if (storage !== null) {
      await storage.remove(KEY.windowSchedule)
      await storage.remove(KEY.windowScheduleManual)
    }
  }
  windowStart.value = next
  if (storage !== null) await saveWindowStart(storage, next)
  if (platform !== null) await platform.stateChanged({ program: program.value, windowStartMillis: next }, напоминания())
  scheduleSync()
}

/**
 * Пересказать площадке текущее состояние окна.
 *
 * Подписка на уведомления живёт в браузере, а серверу её отдаёт запрос расписания —
 * отдельного маршрута нет. Значит, включив уведомления и не тронув окно, человек
 * оставался без них: сервер подписки не видел и пропускал задания. Теперь состояние
 * отправляется сразу — и при включении, и при запуске, если сервер о подписке не знает.
 */
/**
 * ВБ идущего окна, выбранное руками кнопками в таймере (просьба заказчика 31.10.2026).
 *
 * Счёт окон дня ошибается, когда день пошёл не по плану: лишний короткий сон, окно, начатое
 * не тем нажатием. Человек видит, какое окно идёт на самом деле, — и выбирает его. Выбор
 * держится до конца окна: ни самопроверка, ни правка истории его не перебивают. Фазы и
 * напоминания сразу идут по новому графику.
 */
export async function chooseSchedule(k: number): Promise<void> {
  const start = windowStart.value
  if (start === null || k < 0 || k > wakeSchedules.value.length + 1) return
  if (k === windowSchedule.value && выбранВручную.value === start) return
  windowSchedule.value = k
  выбранВручную.value = start
  if (storage !== null) {
    await storage.set(KEY.windowSchedule, String(k))
    await storage.set(KEY.windowScheduleManual, String(start))
  }
  await announceState()
  scheduleSync()
}

export async function announceState(): Promise<void> {
  if (platform !== null) await platform.stateChanged({ program: program.value, windowStartMillis: windowStart.value }, напоминания())
}

export async function saveProfile(next: ChildProfile): Promise<void> {
  await rememberKid(next.name)
  profile.value = next
  if (storage !== null) {
    await saveProfileToStore(storage, next)
    const base = forProfile(next, Date.now())
    const applied = await applyCustomPhases(storage, base)
    customPhases.value = applied.phases === base.phases ? null : applied.phases
    isCustomProgram.value = await isCustomPhases(storage, base.id)
  }
  scheduleSync()
  await persistWindow(null) // новая программа — окно начинаем заново
}

/**
 * Нажатое «Проснулся»: ночью — ночное пробуждение, днём — окно. Решение по настоящим
 * часам в момент нажатия, а не по тикающему сигналу: вкладку, усыплённую браузером с
 * вечера, человек открывает ночью и жмёт раньше, чем часы успевают тикнуть, — и ночью
 * заводилось окно бодрствования с фазами (случай 24.10.2026, 0:03).
 */
export async function wakeUp(): Promise<void> {
  now.value = Date.now()
  // Продлить сон не вышло: бодрствование — с начала попытки, эти минуты уже не сон
  const at = sleepExtendSince.value ?? now.value
  if (isNightWake(at, daySettings.value)) await startNightWake(at)
  else await startWindow(at)
}

export async function startWindow(at = Date.now()): Promise<void> {
  justClosed.value = null
  await записатьСон(null)
  await persistWindow(at)
  if (storage !== null) {
    await logWindow(storage, at)
    windows.value = await loadWindows(storage)
    await recordPhase(at)
    scheduleSync()
  }
}

/**
 * Записывает, на какую фазу перешли. Момент перехода важнее плановой длительности:
 * по нему видно, что фазу оборвали раньше срока или, наоборот, затянули.
 */
/**
 * Начало текущего окна — то самое нажатие «Проснулся».
 *
 * Нельзя брать `windowStart`: в движке нет «текущей фазы», и переход к фазе — это сдвиг
 * начала окна назад. После первого же «Дальше» метка перестаёт совпадать с тем, что
 * записано в истории окон, и записи о конце окна оседали под ключом, которого в истории
 * нет, — то есть конец окна не находился никогда (найдено 23.09.2026).
 *
 * В историю начало пишется один раз, при запуске окна, и не меняется. Его и берём.
 *
 * Но только пока это окно открыто. Окно можно начать и не нажимая «Проснулся»: список фаз
 * нажимается всегда, и человек ставит таймер сразу на ту фазу, где он сейчас, — тогда
 * движок считает начало окна так, будто предыдущие фазы прошли по расписанию. Такое начало
 * в историю не попадало, и все записи нового бодрствования ложились под ключ предыдущего,
 * уже закрытого. В календаре два окна сливались в одно: «12:01–17:43» вместо «12:01–12:56»
 * и «16:32–17:43» (жалоба 09.10.2026).
 *
 * Поэтому правило такое: пока у последнего окна в истории нет конца, все записи его.
 * Есть конец, а таймер идёт — это новое бодрствование, и его начало заводится в истории.
 * Раньше конца предыдущего окна оно начаться не может: расчётное начало бывает и глубоко
 * в прошлом, а окна в истории идут по порядку и не наезжают друг на друга.
 */
async function началоОкна(): Promise<number | null> {
  const s = storage
  if (s === null) return windowStart.value
  const текущее = windowStart.value
  // Спрятанные правкой начала в счёт не идут: иначе записи лягут под ключ, которого
  // в ленте уже нет, а следом заведётся новое начало-двойник (22.10.2026)
  const скрытые = hiddenStarts(events.value)
  const записанные = (await loadWindows(s)).filter((at) => !скрытые.has(at))
  const последнее = записанные.at(-1)

  const завести = async (at: number): Promise<number> => {
    await logWindow(s, at)
    windows.value = await loadWindows(s)
    return at
  }

  // Истории ещё нет: первый таймер на устройстве включили сразу с нужной фазы
  if (последнее === undefined) return текущее === null ? null : завести(текущее)

  const конец = windowClosedAt(events.value, последнее)
  // Прошлое окно ещё открыто — все записи его
  if (конец === null) return последнее
  // Окно не идёт: писать некуда, кроме последнего (так приходит сброс и авто-закрытие)
  if (текущее === null) return последнее
  return завести(Math.max(текущее, конец + 1))
}
async function recordPhase(at: number): Promise<void> {
  const start = await началоОкна()
  if (storage === null || start === null) return
  const index = engine.currentIndex(state.value, at)
  // Окна нет — и фазы нет. Отрицательный индекс сервер отвергал вместе со всей посылкой,
  // и телефон сутки молча слал её снова (23–24.10.2026)
  if (index < 0) return
  const phase = program.value.phases[index]
  await запись(storage, {
    id: at,
    kind: 'phase',
    at,
    window: start,
    index,
    title: phase?.title ?? t('good_night'),
    planned: phase?.minutes ?? 0,
  })
}

/**
 * Человек вернулся к вкладке, которую браузер усыпил вчера вечером, и нажал раньше,
 * чем тикнули часы: нажатие легло бы в окно четырнадцатичасовой давности (случай 08.10.2026,
 * Samsung Internet). Брошенное окно закрываем первым делом — как сделал бы тик.
 */
async function закрытьБрошенное(at: number): Promise<boolean> {
  if (windowStart.value === null || !engine.isAbandoned(state.value, at)) return false
  await abandonWindow()
  return true
}

export async function nextPhase(): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.next(state.value, at).windowStartMillis)
  await recordPhase(at)
}

export async function goToPhase(index: number): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.goTo(state.value, index, at).windowStartMillis)
  await recordPhase(at)
}

export async function goToRitual(): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.toRitual(state.value, at).windowStartMillis)
  await recordPhase(at)
}

/**
 * Ответ на вопрос «Уснул?» — и конец окна.
 *
 * Окно бодрствования кончается в обоих случаях: ритуал позади. Вопрос только в том,
 * чем оно кончилось, — и это как раз то, ради чего события и заводились. Раньше конец
 * окна писал вообще только «Сброс»: аварийный путь записывался, задуманный нет.
 *
 * Закрыть окно может только этот ответ. По времени оно не закрывается: когда план
 * последней фазы выходит, она просто идёт дальше, а кнопка остаётся (правка 26.09.2026) —
 * иначе приложение решало за человека, что малыш уснул.
 */
export async function closeWindow(reason: 'asleep' | 'awake'): Promise<void> {
  const at = Date.now()
  const start = await началоОкна()
  if (storage !== null && start !== null) {
    await запись(storage, { id: at, kind: 'window_end', at, window: start, reason })
  }
  justClosed.value = { reason, at }
  // «Да, уснул» — с этого момента считаются напоминания о подъёме
  await записатьСон(reason === 'asleep' ? at : null)
  await persistWindow(null)
}

let сбрасываем = false

/**
 * Окно без ответа два часа после плана — закрываем сами, причина «без ответа».
 *
 * Это не «уснул за него»: это чтобы следующее «Проснулся» легло чисто и данные не
 * пропадали (решение 26.09.2026). Момент — расчётный, план плюс два часа, а не когда
 * открыли приложение: вкладка могла быть закрыта всё это время.
 */
export async function abandonWindow(): Promise<void> {
  if (сбрасываем) return
  сбрасываем = true
  try {
    const end = engine.windowEndMillis(state.value)
    if (end === null) return
    const at = end + engine.ABANDON_AFTER_SECONDS * 1000
    const start = await началоОкна()
    if (storage !== null && start !== null) {
      await запись(storage, { id: at, kind: 'window_end', at, window: start, reason: 'unanswered' })
    }
    justClosed.value = { reason: 'unanswered', at }
    await записатьСон(null)
    await persistWindow(null)
    // Конец окна — тоже запись для консультантов: не ждём следующего нажатия, чтобы отдать его
    scheduleSync()
  } finally {
    сбрасываем = false
  }
}

/**
 * «Проснулся» ночью — не окно, а ночное пробуждение: без фаз, будильников и пушей.
 * Записывается ради консультантов по сну: ночные пробуждения им важнее всего.
 */
export async function startNightWake(at = Date.now()): Promise<void> {
  nightWake.value = at
  justClosed.value = null
  if (storage === null) return
  await storage.set(KEY.nightWakeStart, String(at))
  await запись(storage, { id: at, kind: 'night_wake', at, window: at })
  scheduleSync()
}

/**
 * Ночное пробуждение оказалось утренним: малыш не уснул обратно, день начался.
 *
 * Запись о ночном помечается удалённой, а окно начинается тем же моментом — временем,
 * когда нажали «Проснулся». Иначе утро пришлось бы начинать заново и терять полчаса.
 */
export async function nightWakeBecameMorning(): Promise<void> {
  const start = nightWake.value
  if (start === null || storage === null) return
  const запись = events.value.find((e) => e.kind === 'night_wake' && e.at === start && e.deleted !== true)
  if (запись !== undefined) await markEventDeleted(storage, запись)
  nightWake.value = null
  await storage.remove(KEY.nightWakeStart)
  await persistWindow(start)
  await logWindow(storage, start)
  windows.value = await loadWindows(storage)
  events.value = await loadEvents(storage)
  await recordPhase(Date.now())
  scheduleSync()
}

/** Конец ночного пробуждения: уснул — или наступило утро, а ответа так и не было. */
export async function endNightWake(reason: 'asleep' | 'unanswered', at = Date.now()): Promise<void> {
  const start = nightWake.value
  if (start === null) return
  nightWake.value = null
  if (storage === null) return
  await storage.remove(KEY.nightWakeStart)
  // Номер на единицу больше начала: при остановленных часах (прогоны) оба момента совпали бы
  await запись(storage, { id: at + 1, kind: 'night_wake_end', at, window: start, reason })
  // Уснул обратно — малыш спит, и экран должен говорить об этом: до этого после ночного
  // «Уснул» показывалось «Готовы?», будто ждём пробуждения (нашёл дымовой прогон 27.10.2026)
  await записатьСон(reason === 'asleep' ? at : null)
  scheduleSync()
}

// Часы идут — сверяем, не пора ли закрыть брошенное: окно без ответа или ночное
// пробуждение, дожившее до утра. Эффект, а не таймер: срабатывает и от тика,
// и от возврата вкладки, и сразу после подключения хранилища.
effect(() => {
  const сейчас = now.value
  if (storage === null) return
  if (windowStart.value !== null && engine.isAbandoned(state.value, сейчас)) void abandonWindow()
  const ночное = nightWake.value
  const утро = ночное === null ? null : nextDayStart(ночное, daySettings.value)
  if (ночное !== null && утро !== null && сейчас >= утро) void endNightWake('unanswered', утро)
})
export async function resetWindow(): Promise<void> {
  const at = Date.now()
  const start = await началоОкна()
  // Конец окна хранится записью, а не в списке начал: формат `windows_<месяц>`
  // читают часы и виджет Android, менять его нельзя.
  if (storage !== null && start !== null) {
    await запись(storage, { id: at, kind: 'window_end', at, window: start, reason: 'reset' })
  }
  await записатьСон(null)
  await persistWindow(null)
}

/** `null` — вернуть рекомендованные фазы. */
/**
 * Правки «Моего дня» и списки ВБ уезжают на сервер внутри профиля, и спор «чья запись
 * свежее» решается его меткой. Значит менять их, не трогая метку, нельзя: сервер считал бы
 * свою копию не старее и возвращал прежнее состояние — добавленное окно бодрствования
 * пропадало при следующем обмене (найдено дымовым прогоном по проду 27.10.2026).
 */
async function пометитьПрофильИзменённым(): Promise<void> {
  if (storage !== null) await storage.set(KEY.profileUpdatedAt, String(Date.now()))
}

async function сохранитьГрафики(list: WakeSchedule[]): Promise<void> {
  wakeSchedules.value = list
  if (storage !== null) await saveSchedules(storage, baseProgram.value.id, list)
  await пометитьПрофильИзменённым()
  scheduleSync()
  await announceState()
}

/** «+ ВБ»: новая карточка копирует ВБ 1 (с правками), имя — «ВБ N». */
export async function addSchedule(): Promise<void> {
  const первое = programForSchedule(baseProgram.value, customPhases.value, wakeSchedules.value, 1)
  const k = wakeSchedules.value.length + 2
  await сохранитьГрафики([...wakeSchedules.value, { name: defaultScheduleName(k, t('schedule_short')), phases: первое.phases.map((p) => ({ ...p })) }])
}

/** [index] — место в списке добавленных (0 — это ВБ 2). */
export async function renameSchedule(index: number, name: string): Promise<void> {
  const list = wakeSchedules.value.map((s, i) => (i === index ? { ...s, name } : s))
  await сохранитьГрафики(list)
}

export async function removeSchedule(index: number): Promise<void> {
  await сохранитьГрафики(wakeSchedules.value.filter((_, i) => i !== index))
}

/** Сколько спать перед ВБ с [index] в списке добавленных; null — не задано. */
export async function setScheduleNap(index: number, minutes: number | null): Promise<void> {
  const list = wakeSchedules.value.map((s, i) => {
    if (i !== index) return s
    const { napBefore: _было, ...без } = s
    return minutes === null ? без : { ...без, napBefore: minutes }
  })
  await сохранитьГрафики(list)
}

/** Фазы ВБ с номером 2…N; null — вернуть рекомендованные по возрасту. */
export async function setSchedulePhases(index: number, phases: Phase[] | null): Promise<void> {
  const list = wakeSchedules.value.map((s, i) => (i === index ? { ...s, phases: (phases ?? baseProgram.value.phases).map((p) => ({ ...p })) } : s))
  await сохранитьГрафики(list)
}

export async function setPhases(phases: Phase[] | null): Promise<void> {
  customPhases.value = phases
  isCustomProgram.value = phases !== null
  if (storage === null) return
  const id = baseProgram.value.id
  if (phases === null) await clearCustomPhases(storage, id)
  else await saveCustomPhases(storage, id, phases)
  await пометитьПрофильИзменённым()
  scheduleSync()
}

export async function addNote(note: Note): Promise<void> {
  if (storage === null) return
  await addNoteToStore(storage, { ...note, updatedAt: Date.now() })
  notes.value = await loadNotes(storage)
  scheduleSync()
}

export async function updateNote(note: Note): Promise<void> {
  if (storage === null) return
  await updateNoteInStore(storage, { ...note, updatedAt: Date.now() })
  notes.value = await loadNotes(storage)
  scheduleSync()
}

export async function removeNote(note: Note): Promise<void> {
  if (storage === null) return
  await removeNoteFromStore(storage, note)
  notes.value = await loadNotes(storage)
  scheduleSync()
}
